/**
 * @fileoverview Syndicator Showdown — Race Results Entry Panel
 * Dead heat logic: checkbox + count per position, automatic position locking,
 * Australian standard prize splitting (combined positions ÷ runners).
 * Each dead-heat position now renders one dropdown per participant.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NonRunnerPanel } from '../../components/admin/NonRunnerPanel';
import '../../components/admin/NonRunnerPanel.css';
import {
  collection, query, orderBy,
  onSnapshot, doc, getDoc, updateDoc,
  serverTimestamp, increment,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../services/firebase';
import './AdminResultsPage.css';

const POSITION_KEYS   = ['first', 'second', 'third', 'fourth', 'fifth'];
const POSITION_LABELS = ['1ST', '2ND', '3RD', '4TH', '5TH'];
const POSITIONS       = ['pos1', 'pos2', 'pos3', 'pos4', 'pos5'];

const POS_LABELS = {
  pos1: '1st Place', pos2: '2nd Place', pos3: '3rd Place',
  pos4: '4th Place', pos5: '5th Place',
};

// ─── Dead Heat Utility Functions ──────────────────────────────────────────────

/**
 * Calculate which positions are locked by dead heats above them.
 * A 2-way DH at 1st consumes 1st + 2nd prize slots → pos2 is locked.
 * Works with the new { runnerIds, deadHeat, deadHeatCount } shape.
 */
function getLockedPositions(positions) {
  const locked = { pos2: false, pos3: false, pos4: false, pos5: false };
  let nextAvailable = 2;

  for (const pos of POSITIONS) {
    const posNum = parseInt(pos.replace('pos', ''));
    if (posNum < nextAvailable) {
      if (posNum > 1) locked[pos] = true;
      continue;
    }
    const { deadHeat, deadHeatCount } = positions[pos] || {};
    if (deadHeat && deadHeatCount > 1) {
      nextAvailable = posNum + deadHeatCount;
    } else {
      nextAvailable = posNum + 1;
    }
  }
  return locked;
}

/**
 * Calculate prize money for each placed runner using Australian dead heat rules.
 * Dead heaters share the combined prize of the positions they consume, split equally.
 *
 *   2-way DH at 1st  → each gets (1st + 2nd) ÷ 2
 *   3-way DH at 1st  → each gets (1st + 2nd + 3rd) ÷ 3
 */
function calculateDeadHeatPrizes(positions, prizes) {
  const prizeByPos = [
    prizes.first, prizes.second, prizes.third, prizes.fourth, prizes.fifth,
  ];
  const payouts = {};
  const locked  = getLockedPositions(positions);
  let   prizeIdx = 0;

  for (const pos of POSITIONS) {
    if (locked[pos]) continue;

    const { runnerIds = [''], deadHeat, deadHeatCount } = positions[pos];
    const activeIds = runnerIds.filter(Boolean);

    if (!activeIds.length) { prizeIdx++; continue; }

    if (deadHeat && deadHeatCount > 1) {
      let combined = 0;
      for (let i = 0; i < deadHeatCount; i++) combined += prizeByPos[prizeIdx + i] || 0;
      const perRunner = combined / deadHeatCount;
      activeIds.forEach(id => { payouts[id] = (payouts[id] || 0) + perRunner; });
      prizeIdx += deadHeatCount;
    } else {
      activeIds.forEach(id => {
        payouts[id] = (payouts[id] || 0) + (prizeByPos[prizeIdx] || 0);
      });
      prizeIdx++;
    }
  }
  return payouts;
}

// ─── Results Entry Form ───────────────────────────────────────────────────────

const emptyPos = () => ({ runnerIds: [''], deadHeat: false, deadHeatCount: 2 });

const INITIAL_POSITIONS = {
  pos1: emptyPos(), pos2: emptyPos(), pos3: emptyPos(),
  pos4: emptyPos(), pos5: emptyPos(),
};

const ResultsForm = ({ runners, prizes, onSubmit }) => {
  const [positions, setPositions] = useState(INITIAL_POSITIONS);

  const locked  = useMemo(() => getLockedPositions(positions), [positions]);
  const payouts = useMemo(() => calculateDeadHeatPrizes(positions, prizes), [positions, prizes]);

  // Duplicate runner detection — collect every selected ID, flag any that appear more than once
  const duplicateIds = useMemo(() => {
    const allSelected = POSITIONS.flatMap(pos => positions[pos].runnerIds.filter(Boolean));
    const seen  = new Set();
    const dupes = new Set();
    allSelected.forEach(id => { seen.has(id) ? dupes.add(id) : seen.add(id); });
    return dupes;
  }, [positions]);

  const hasDuplicates = duplicateIds.size > 0;

  const prizeValues = [
    prizes.first, prizes.second, prizes.third, prizes.fourth, prizes.fifth,
  ];

  // Update a single runnerId within a position's array
  const setRunner = (pos, idx, value) => {
    setPositions(prev => {
      const ids = [...prev[pos].runnerIds];
      ids[idx] = value;
      return { ...prev, [pos]: { ...prev[pos], runnerIds: ids } };
    });
  };

  // Toggle dead heat — expand or collapse runner slots
  const toggleDH = (pos, checked) => {
    setPositions(prev => ({
      ...prev,
      [pos]: {
        ...prev[pos],
        deadHeat:  checked,
        runnerIds: Array(checked ? prev[pos].deadHeatCount : 1).fill(''),
      },
    }));
  };

  // Change dead heat count — resize the runner slots array
  const setDHCount = (pos, count) => {
    setPositions(prev => {
      const current = prev[pos].runnerIds;
      const newIds  = Array(count).fill('').map((_, i) => current[i] || '');
      return { ...prev, [pos]: { ...prev[pos], deadHeatCount: count, runnerIds: newIds } };
    });
  };

  // Prize display for a dead-heat row
  const dhDisplayPrize = (posIdx, data) => {
    if (!data.deadHeat) return null;
    let combined = 0;
    for (let i = 0; i < data.deadHeatCount && posIdx + i < 5; i++) {
      combined += prizeValues[posIdx + i] || 0;
    }
    return combined / data.deadHeatCount;
  };

  const canSubmit = positions.pos1.runnerIds.some(Boolean) && !hasDuplicates;

  const availableRunners = runners
    .filter(r => r.status?.toLowerCase() !== 'scratched' && !r.scratched)
    .sort((a, b) => (a.runnerNumber || 0) - (b.runnerNumber || 0));

  return (
    <div className="ss-results-form">
      {POSITIONS.map((pos, posIdx) => {
        const data     = positions[pos];
        const isLocked = locked[pos];
        const dhPrize  = dhDisplayPrize(posIdx, data);

        return (
          <div key={pos} className={`ss-result-row ${isLocked ? 'ss-result-row--locked' : ''}`}>

            <div className="ss-result-header">
              <span className="ss-result-label">
                {POS_LABELS[pos]}
                {isLocked && (
                  <span className="ss-locked-text">LOCKED BY DEAD HEAT ABOVE</span>
                )}
              </span>

              {!isLocked && (
                <div className="ss-result-dh-controls">
                  <label className="ss-dh-check">
                    <input
                      type="checkbox"
                      checked={data.deadHeat}
                      onChange={e => toggleDH(pos, e.target.checked)}
                    />
                    Dead Heat
                  </label>

                  {data.deadHeat && (
                    <select
                      className="ss-dh-count-sel"
                      value={data.deadHeatCount}
                      onChange={e => setDHCount(pos, Number(e.target.value))}
                    >
                      <option value={2}>2-way</option>
                      <option value={3}>3-way</option>
                      {posIdx >= 3 && <option value={4}>4-way</option>}
                    </select>
                  )}

                  <span className="ss-result-prize">
                    {data.deadHeat && dhPrize !== null ? (
                      <span className="ss-dh-prize">
                        Each gets ${dhPrize.toLocaleString('en-AU')}
                        <small> ({data.deadHeatCount}-way split)</small>
                      </span>
                    ) : (
                      <span>${(prizeValues[posIdx] || 0).toLocaleString('en-AU')}</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* One dropdown per runner slot */}
            {!isLocked && data.runnerIds.map((rId, idx) => {
              const isDupe = !!(rId && duplicateIds.has(rId));
              const dupeName = isDupe
                ? (availableRunners.find(r => r.id === rId)?.name || 'This runner')
                : null;
              return (
                <div key={idx} className="ss-runner-select-row">
                  {data.deadHeat && (
                    <span className="ss-runner-slot-label">Runner {idx + 1}</span>
                  )}
                  <select
                    className="ss-res-runner-select"
                    value={rId}
                    onChange={e => setRunner(pos, idx, e.target.value)}
                    style={isDupe ? { border: '2px solid #ef4444' } : undefined}
                  >
                    <option value="">— Select runner —</option>
                    {availableRunners.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.runnerNumber}. {r.name}
                      </option>
                    ))}
                  </select>
                  {isDupe && (
                    <div style={{
                      color: '#ef4444', fontSize: '12px', marginTop: '4px',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      ⚠ {dupeName} is already selected in another position
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        );
      })}

      {hasDuplicates && (
        <div style={{
          backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '12px',
          color: '#fca5a5', fontSize: '13px',
        }}>
          ❌ Cannot submit — duplicate runners detected in one or more positions. Check highlighted fields above.
        </div>
      )}
      <button
        type="button"
        className={`ss-res-confirm-btn ${canSubmit ? '' : 'ss-res-confirm-btn--disabled'}`}
        onClick={() => canSubmit && onSubmit(positions, payouts)}
        disabled={!canSubmit}
      >
        Review &amp; Confirm Results
      </button>
    </div>
  );
};

// ─── Race Result Card ─────────────────────────────────────────────────────────

const RaceResultCard = ({ race, slateId, prizePool, onResulted }) => {
  const [runners,        setRunners]        = useState([]);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState(null);
  const [confirming,     setConfirming]     = useState(false);
  const [pendingResults, setPendingResults] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState(null);

  // Duplicate check in confirm modal — recomputes whenever pendingResults changes
  const confirmDuplicateIds = useMemo(() => {
    if (!pendingResults) return new Set();
    const allSelected = POSITIONS.flatMap(pos => pendingResults[pos].runnerIds.filter(Boolean));
    const seen  = new Set();
    const dupes = new Set();
    allSelected.forEach(id => { seen.has(id) ? dupes.add(id) : seen.add(id); });
    return dupes;
  }, [pendingResults]);

  useEffect(() => {
    if (!race?.id || !slateId) return;
    getDoc(doc(db, 'game_slates', slateId, 'races', race.id)).then(snap => {
      if (snap.exists()) setRunners(snap.data().runners || []);
    });
  }, [race?.id, slateId]);

  const handleFormSubmit = useCallback((positions, payouts) => {
    setPendingResults(positions);
    setPendingPayouts(payouts);
    setConfirming(true);
  }, []);

  const handleConfirm = async () => {
    if (!pendingResults || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const lockedPos   = getLockedPositions(pendingResults);
      const hasDeadHeat = POSITIONS.some(pos =>
        !lockedPos[pos] && pendingResults[pos].deadHeat &&
        pendingResults[pos].runnerIds.some(Boolean)
      );

      // Build one result entry per runner (multiple per position in a DH)
      const firestoreResults = [];
      POSITIONS.forEach(pos => {
        if (lockedPos[pos]) return;
        const { runnerIds, deadHeat } = pendingResults[pos];
        const activeIds = runnerIds.filter(Boolean);
        if (!activeIds.length) return;
        const posNum = parseInt(pos.replace('pos', ''));
        activeIds.forEach(runnerId => {
          const runner = runners.find(r => r.id === runnerId);
          firestoreResults.push({
            position:     posNum,
            runnerId,
            runnerName:   runner?.name ?? '',
            runnerNumber: runner?.runnerNumber ?? null,
            prizeAmount:  pendingPayouts[runnerId] || 0,
            deadHeat:     !!deadHeat,
          });
        });
      });

      // adjustedPrizePool must store RAW position prizes only.
      // The settleRaceResult Cloud Function applies equityPercent itself.
      // pendingPayouts is equity-weighted — using it here caused double-counting.
      const adjustedPrizePool = {};
      const prizeValues = [
        prizePool.first, prizePool.second, prizePool.third,
        prizePool.fourth, prizePool.fifth,
      ];
      POSITION_KEYS.forEach((key, i) => {
        const pos     = `pos${i + 1}`;
        const posData = pendingResults[pos] || {};
        const { runnerIds, deadHeat, deadHeatCount } = posData;
        const firstId = (runnerIds || []).find(Boolean);

        if (!lockedPos[pos] && firstId) {
          if (deadHeat && deadHeatCount > 1) {
            // Dead heat: split the combined prize pool equally — raw per-runner prize
            let combined = 0;
            for (let j = 0; j < deadHeatCount; j++) {
              combined += prizeValues[i + j] || 0;
            }
            adjustedPrizePool[key] = Math.round(combined / deadHeatCount);
          } else {
            // Normal result: raw position prize, no equity weighting
            adjustedPrizePool[key] = prizeValues[i] || 0;
          }
        } else {
          adjustedPrizePool[key] = prizePool[key] || 0;
        }
      });

      // Build officialPlacings as uppercase horse names — matches race_allocations runner keys
      const officialPlacings = firestoreResults
        .sort((a, b) => a.position - b.position)
        .map(r => r.runnerName.trim().toUpperCase());

      await updateDoc(doc(db, 'game_slates', slateId, 'races', race.id), {
        status:            'resulted',
        results:           firestoreResults,
        result: {
          officialPlacings,
          isDeadHeat:        hasDeadHeat,
          deadHeatPositions: hasDeadHeat
            ? [...new Set(firestoreResults.filter(r => r.deadHeat).map(r => r.position))]
            : [],
        },
        deadHeat:          hasDeadHeat,
        deadHeatPositions: hasDeadHeat
          ? [...new Set(firestoreResults.filter(r => r.deadHeat).map(r => r.position))]
          : [],
        adjustedPrizePool,
        resultedAt:        serverTimestamp(),
        resultedByAdmin:   true,
      });

      // Increment completedRaces on the parent slate so the dashboard
      // progress bar stays in sync without a separate query
      await updateDoc(doc(db, 'game_slates', slateId), {
        completedRaces: increment(1),
      });

      onResulted(race.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  const isAlreadyResulted = race.status === 'resulted';

  return (
    <div className={`ss-res-race-card ${isAlreadyResulted ? 'ss-res-race-card--settled' : ''}`}>
      <div className="ss-res-race-header">
        <div>
          <span className="ss-res-leg-badge">R{race.raceNumber}</span>
          <span className="ss-res-race-name">{race.raceName || `Race ${race.raceNumber}`}</span>
        </div>
        <span className={`ss-res-status-pill ss-res-status-pill--${race.status || 'pending'}`}>
          {(race.status || 'PENDING').toUpperCase()}
        </span>
      </div>

      {isAlreadyResulted ? (
        <div className="ss-res-settled-view">
          <p className="ss-res-settled-label">✓ Results entered</p>
          {race.results?.sort((a, b) => a.position - b.position).map((r, i) => (
            <div key={i} className="ss-res-settled-row">
              <span className="ss-res-settled-pos">{POSITION_LABELS[r.position - 1]}</span>
              <span className="ss-res-settled-name">{r.runnerNumber}. {r.runnerName}</span>
              <span className="ss-res-settled-prize">${(r.prizeAmount || 0).toLocaleString('en-AU')}</span>
              {race.deadHeatPositions?.includes(r.position) && (
                <span className="ss-res-dh-tag">DH</span>
              )}
            </div>
          ))}
          <NonRunnerPanel race={race} slateId={slateId} />
        </div>

      ) : confirming && pendingResults ? (
        <div className="ss-res-confirm-block">
          {confirmDuplicateIds.size > 0 && (
            <div style={{
              backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
              borderRadius: '8px', padding: '12px 16px', marginBottom: '12px',
              color: '#fca5a5', fontSize: '13px',
            }}>
              ❌ Cannot confirm — duplicate runners detected:
              {[...confirmDuplicateIds].map(id => {
                const runner = runners.find(r => r.id === id);
                return (
                  <div key={id} style={{ marginTop: '4px' }}>
                    ⚠ {runner?.name || id} appears in multiple positions
                  </div>
                );
              })}
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#f87171' }}>
                Go back and fix the highlighted fields before confirming.
              </div>
            </div>
          )}
          <p className="ss-res-confirm-warning">
            ⚠ This will settle the race and pay dividends. Cannot be undone.
          </p>

          <div className="ss-res-preview">
            <h4 className="ss-res-preview-title">Confirm Results</h4>
            {(() => {
              const lp = getLockedPositions(pendingResults);
              return POSITIONS.flatMap(pos => {
                if (lp[pos]) return [];
                const { runnerIds, deadHeat, deadHeatCount } = pendingResults[pos];
                const activeIds = runnerIds.filter(Boolean);
                if (!activeIds.length) return [];
                return activeIds.map((runnerId, idx) => {
                  const runner = runners.find(r => r.id === runnerId);
                  const prize  = pendingPayouts[runnerId] || 0;
                  return (
                    <div
                      key={`${pos}-${idx}`}
                      className={`ss-res-preview-row ${deadHeat ? 'ss-res-preview-row--dh' : ''}`}
                    >
                      <span className="ss-res-preview-pos">
                        {POS_LABELS[pos]}{deadHeat && idx > 0 ? ` (=${idx + 1})` : ''}
                      </span>
                      <span className="ss-res-preview-name">
                        {runner ? `${runner.runnerNumber}. ${runner.name}` : '—'}
                      </span>
                      <span className="ss-res-preview-prize">
                        ${prize.toLocaleString('en-AU')}
                        {deadHeat && idx === 0 && (
                          <span className="ss-res-preview-orig"> ({deadHeatCount}-way DH)</span>
                        )}
                      </span>
                    </div>
                  );
                });
              });
            })()}
          </div>

          {error && <div className="ss-res-error">⚠ {error}</div>}

          <div className="ss-res-confirm-actions">
            <button
              className={`ss-res-confirm-btn ss-res-confirm-btn--final ${confirmDuplicateIds.size > 0 ? 'ss-res-confirm-btn--disabled' : ''}`}
              onClick={handleConfirm}
              disabled={submitting || confirmDuplicateIds.size > 0}
              type="button"
            >
              {submitting ? '⏳ Settling…' : '✓ Confirm & Settle Race'}
            </button>
            <button
              className="ss-res-cancel-btn"
              onClick={() => {
                setConfirming(false);
                setPendingResults(null);
                setPendingPayouts(null);
              }}
              type="button"
            >
              Go back
            </button>
          </div>
        </div>

      ) : (
        <>
          <ResultsForm runners={runners} prizes={prizePool} onSubmit={handleFormSubmit} />
          {error && <div className="ss-res-error">⚠ {error}</div>}
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const AdminResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slateId = searchParams.get('slate') || '';

  const [currentUser, setCurrentUser] = useState(null);
  const [slates,      setSlates]      = useState([]);
  const [slateData,   setSlateData]   = useState(null);
  const [races,       setRaces]       = useState([]);
  const [, setResultedIds]            = useState(new Set());

  useEffect(() => { return onAuthStateChanged(auth, setCurrentUser); }, []);

  useEffect(() => {
    const q = query(collection(db, 'game_slates'), orderBy('meetingDate', 'desc'));
    return onSnapshot(q, snap => setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  // Active (non-ended, non-archived) slates only — filter client-side
  const activeSlates = useMemo(() =>
    slates
      .filter(s =>
        s.finalised !== true &&
        s.status !== 'finalised' &&
        s.status !== 'completed' &&
        s.status !== 'resulted' &&
        s.archived !== true
      )
      .sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || ''))
  , [slates]);

  // Auto-select first active slate when the current selection is no longer active
  useEffect(() => {
    if (activeSlates.length === 0) return;
    if (slateId && activeSlates.find(s => s.id === slateId)) return;
    setSearchParams({ slate: activeSlates[0].id });
  }, [activeSlates, slateId]);

  useEffect(() => {
    if (!slateId) { setRaces([]); setSlateData(null); return; }
    const slateUnsub = onSnapshot(doc(db, 'game_slates', slateId), snap => {
      setSlateData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    const q = query(
      collection(db, 'game_slates', slateId, 'races'),
      orderBy('raceNumber', 'asc')
    );
    const raceUnsub = onSnapshot(q, snap =>
      setRaces(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { slateUnsub(); raceUnsub(); };
  }, [slateId]);

  const handleResulted = useCallback((raceId) => {
    setResultedIds(prev => new Set([...prev, raceId]));
  }, []);

  const prizePool    = slateData?.defaultPrizePool || { first: 500000, second: 200000, third: 100000, fourth: 60000, fifth: 40000 };
  const pendingRaces = races.filter(r => r.status !== 'resulted');
  const settledRaces = races.filter(r => r.status === 'resulted');

  if (!currentUser) return (
    <div className="ss-res-page"><div className="ss-res-auth">Admin sign-in required.</div></div>
  );

  return (
    <div className="ss-res-page">
      <div className="ss-res-page-header">
        <div>
          <h1 className="ss-res-page-title">Results Entry</h1>
          <p className="ss-res-page-sub">Enter finishing positions to settle races and pay dividends</p>
        </div>
      </div>

      <div className="ss-res-slate-selector">
        <label className="ss-res-selector-label">Select game</label>
        <select
          className="ss-res-selector"
          value={slateId}
          onChange={e => setSearchParams({ slate: e.target.value })}
        >
          <option value="">— Choose a game —</option>
          {activeSlates.map(s => (
            <option key={s.id} value={s.id}>{s.gameName || s.id} · {s.meetingDate}</option>
          ))}
        </select>
      </div>

      {slateId && (
        <>
          {races.length > 0 && (
            <div className="ss-res-progress-strip">
              <div className="ss-res-progress-left">
                <span>{settledRaces.length} of {races.length} races settled</span>
                <div className="ss-res-progress-bar">
                  <div
                    className="ss-res-progress-fill"
                    style={{ width: `${(settledRaces.length / races.length) * 100}%` }}
                  />
                </div>
              </div>
              {/* Mark as Finalised — enabled once all races are settled */}
              {slateData?.finalised === true ? (
                <span className="ss-res-finalised-badge">✓ Finalised</span>
              ) : (
                <button
                  type="button"
                  className={`ss-res-finalise-btn ${pendingRaces.length === 0 ? '' : 'ss-res-finalise-btn--disabled'}`}
                  disabled={pendingRaces.length > 0}
                  title={pendingRaces.length > 0 ? `${pendingRaces.length} races still need results` : 'Mark this game as fully finalised'}
                  onClick={async () => {
                    if (!window.confirm('Mark this game as Finalised? This will lock the results.')) return;
                    try {
                      await updateDoc(doc(db, 'game_slates', slateId), {
                        finalised:       true,
                        finalisedAt:     serverTimestamp(),
                        completedRaces:  races.length,
                        status:          'finalised', // triggers awardShowdownPoints
                      });
                    } catch (err) {
                      alert(`Failed to finalise: ${err.message}`);
                    }
                  }}
                >
                  {pendingRaces.length > 0
                    ? `Finalise (${pendingRaces.length} pending)`
                    : '✓ Mark as Finalised'}
                </button>
              )}
            </div>
          )}

          {pendingRaces.length > 0 && (
            <section className="ss-res-section">
              <h2 className="ss-res-section-title">Awaiting Results ({pendingRaces.length})</h2>
              {pendingRaces.map(race => (
                <RaceResultCard
                  key={race.id} race={race} slateId={slateId}
                  prizePool={prizePool} onResulted={handleResulted}
                />
              ))}
            </section>
          )}

          {settledRaces.length > 0 && (
            <section className="ss-res-section">
              <h2 className="ss-res-section-title">Settled ({settledRaces.length})</h2>
              {settledRaces.map(race => (
                <RaceResultCard
                  key={race.id} race={race} slateId={slateId}
                  prizePool={prizePool} onResulted={handleResulted}
                />
              ))}
            </section>
          )}

          {races.length === 0 && (
            <div className="ss-res-empty">No races found for this game.</div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminResultsPage;
